import { Component, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { currentAnnotationActions } from '@store/doc-viewer/annotation-editor/annotation-editor.actions';
import { currentAnnotationId, currentActiveAnnotations, currentColors, isManualAnnotation } from '@store/doc-viewer/annotation-editor/annotation-editor.selectors';
import { ComparisonUtility } from '@utilities/comparison.utility';
import { cloneDeep } from 'lodash-es';
import { Observable, distinctUntilChanged, map, takeUntil } from 'rxjs';
import { BaseComponent } from 'src/app/components/base.component';
import { HighlightColor } from 'src/app/components/review/redaction-annotation/highlight-color.model';
import { LifeCycleEdit } from 'src/app/components/review/redaction-annotation/life-cycle-edit.enum';
import { RedactionAnnotationEdit } from 'src/app/components/review/redaction-annotation/redaction-annotation-edit.model';

@Component({
    selector: 'lex-annotation-list',
    templateUrl: './annotation-list.component.html',
    styleUrls: ['./annotation-list.component.scss'],
    standalone: false
})
export class AnnotationListComponent extends BaseComponent implements OnInit {
	public showManualMessage: boolean = false;
	public annotations: RedactionAnnotationEdit[] = [];
	public currentAnnotationId: number;
	public pageAnnotationMap: Array<{ pageNum: number, annotations: RedactionAnnotationEdit[] }> = new Array<{
		pageNum: number,
		annotations: RedactionAnnotationEdit[]
	}>();
	public editItemTextId: number = null;
	public editItemColorId: number = null;
	public annotationColors: HighlightColor[] = [];

	private annotations$: Observable<RedactionAnnotationEdit[]> = this.store.select(currentActiveAnnotations);
	private colors$: Observable<HighlightColor[]> = this.store.select(currentColors);
	private currentId$: Observable<number> = this.store.select(currentAnnotationId);
	private manualAnnotation$: Observable<boolean> = this.store.select(isManualAnnotation);
	private tempAnnotationText: string = null;

	constructor(private store: Store) {
		super();
	}

	ngOnInit(): void {
		this.annotations$.pipe(
			distinctUntilChanged((a, b) => ComparisonUtility.isEqual(a, b)),
			map((annotations) => this.updatePageAnnotationMap(annotations)),
			takeUntil(this.destroy$)
		).subscribe();

		this.currentId$.pipe(
			distinctUntilChanged((a, b) => ComparisonUtility.isEqual(a, b)),
			map((id) => this.currentAnnotationId = id),
			takeUntil(this.destroy$)
		).subscribe();

		this.colors$.pipe(
			distinctUntilChanged((a, b) => ComparisonUtility.isEqual(a, b)),
			map((colors) => this.annotationColors = colors),
			takeUntil(this.destroy$)
		).subscribe();
		this.manualAnnotation$.pipe(
			takeUntil(this.destroy$)
		).subscribe((hasPdf) => this.showManualMessage = hasPdf);
	}

	public getActiveAnnotationCount(annotations: RedactionAnnotationEdit[]): number {
		return annotations.filter(e => e.status != LifeCycleEdit.Deleted).length;
	}

	public setSelectedAnnotationId(id: number): void {
		setTimeout(() => this.store.dispatch(currentAnnotationActions.setCurrentAnnotation({annotationId: id})));
	}

	public updateAnnotation(edit: RedactionAnnotationEdit): void {
		this.store.dispatch(currentAnnotationActions.updateAnnotation({annotation: edit}));
		this.editItemTextId = null;
	}

	public deleteAnnotation(annotation: RedactionAnnotationEdit): void {
		this.store.dispatch(currentAnnotationActions.deleteAnnotation({annotationId: annotation.clientId}));
	}

	public setAnnotationEditTextId(annotation: RedactionAnnotationEdit): void {
		this.editItemTextId = annotation.clientId;
		this.tempAnnotationText = annotation.text;
	}

	public setAnnotationText(annotation: RedactionAnnotationEdit, $event = null): void {
		if ($event) {
			$event.preventDefault();
		}
		this.updateAnnotation(annotation);
	}

	public cancelTextEdit(annotation: RedactionAnnotationEdit): void {
		this.editItemTextId = null;
		annotation.text = this.tempAnnotationText;
	}

	public setColor(color: HighlightColor, edit: RedactionAnnotationEdit) {
		edit.highlightColor = color.colorValue;
		edit.highlightColorId = color.highlightColorId;
		this.store.dispatch(currentAnnotationActions.setColorId({colorId: color.highlightColorId}));
		this.updateAnnotation(edit);
	}

	public getBackGroundColor(colorValue: string): string {
		return `#${colorValue}`;
	}

	public trackByPageNumber(index: number, value: any): number {
		return value.pageNum;
	}

	public trackById(index: number, value: RedactionAnnotationEdit): number {
		return value.clientId;
	}

	private updatePageAnnotationMap(annotations: RedactionAnnotationEdit[]): void {
		const localAnnotations = cloneDeep(annotations);
		const targetAnnotations = localAnnotations?.sort((a: RedactionAnnotationEdit, b: RedactionAnnotationEdit) => {
			if (a.y === b.y)
				if (a.x === b.x)
					return a.clientId > b.clientId ? 1 : -1;
				else
					return a.x > b.x ? 1 : -1;
			return a.y > b.y ? 1 : -1;
		});
		this.pageAnnotationMap = [];
		targetAnnotations?.forEach(r => {
			if (this.pageAnnotationMap.findIndex(e => e.pageNum == r.rank) != -1)
				this.pageAnnotationMap.find(e => e.pageNum == r.rank).annotations.push(r);
			else
				this.pageAnnotationMap.push({
					pageNum: r.rank,
					annotations: [r]
				});
		});
		this.pageAnnotationMap?.sort((a, b) => a.pageNum > b.pageNum ? 1 : -1);
	}
}
